import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import moment from 'moment';

//Components
import {
    SidePanel,
    SidePanelBody,
    SidePanelFooter,
    Button,
    TimeFrame,
    useTimeFrameComponent,
    Input,
    Form
} from '@gpstrackit/gtc-react-controls';
import { Grid } from '@mui/material';

// Actions
import {
    getDriverPerformanceDriverSearchEvents,
    reset
} from './redux/slice';

import { openModalAdvancedFilters } from './redux/slice';

//Helpers
import { getInitialRange } from '../../../../.globals/helpers'

import TagsManage from "Modules/.common/components/TagsManage";
// Styles
import './index.scss';
const AdvancedFilters = ({ tableRef, formData, setFormData, initialState, applyChanges, hasFilters, savePreferences, loading }) => {

    const dispatch = useDispatch()
    const { messages } = useIntl()

    // Local state for temporary filter values (only applied on "Apply" button)
    const [tempFilters, setTempFilters] = useState({});
    const [entitySelected, setEntitySelected] = useState([]);

    // Redux States
    const { open, events, loadingEvents } = useSelector((state) => state.driverPerformanceDriverTabAdvancedFiltersRedux);
    const dateformat = useSelector(state => state.securityRedux.user.dateformat);

    const timeFrameOptions = [
        { id: "last7days", label: messages['date_last_7days'] },
        { id: "last15days", label: messages['date_last_15days'] },
        { id: "last30Days", label: messages['date_last_30days'] },
        { id: "custom", label: messages['date_custom'] },
    ];

    const timeFrame = useTimeFrameComponent(timeFrameOptions, "last7days")

    //Form refs
    const formRef = Form.useForm();

    // Callback to handle changes in TagsManage
    const onChangeEntityTagPicker = useCallback(
        (items, entity) => {
            let newItems = [];

            if (items.length) {
                newItems = items.filter((n) => n.id != -1);
            } else {
                newItems = [];
            }

            // Separate tags and drivers according to the "name" field
            const tags = newItems.filter(item => item.name === "Tag");
            const drivers = newItems.filter(item => item.name === "Driver");

            // Update tempFilters with separated tags and drivers
            setTempFilters({
                ...tempFilters,
                tags: tags.map(tag => ({ id: tag.id, label: tag.label })),
                drivers: drivers.map(driver => ({
                    driverId: driver.id,
                    name: driver.label,
                }))
            });

            setEntitySelected(newItems);
        },
        [tempFilters]
    );

    // Definir entidades disponibles
    const entities = useMemo(
        () => [
            { id: "Tag", label: messages["driver_performance_events_filters_tags"] },
            { id: "Driver", label: messages["clipRequestAdvancedFilters_drivers"] },
        ],
        [messages]
    );

    // Sync tempFilters when formData changes or modal opens
    useEffect(() => {
        if (open && formData) {
            // Initialize tempFilters with current formData values when modal opens
            setTempFilters({ ...formData });

            // Synchronize entitySelected with formData values
            const selectedEntities = [];

            // Add tags to entitySelected
            if (formData?.tags && formData.tags.length > 0) {
                formData.tags.forEach(tag => {
                    selectedEntities.push({
                        id: tag.id,
                        label: tag.label,
                        name: "Tag",
                        parentId: tag.parentId || null
                    });
                });
            }

            // Add drivers to entitySelected
            if (formData?.drivers && formData.drivers.length > 0) {
                formData.drivers.forEach(driver => {
                    selectedEntities.push({
                        id: driver.driverId,
                        label: driver.name,
                        name: "Driver"
                    });
                });
            }

            setEntitySelected(selectedEntities);

            if (formData?.timeFrame && formData?.rangeDate?.start && formData?.rangeDate?.end) {
                let currentTime = timeFrameOptions.find((item) => item?.id === formData.timeFrame)
                
                timeFrame.onChangeSelect(currentTime, formData?.rangeDate?.start, formData?.rangeDate?.end)
            }
            formRef?.setValues(formData);
        }
        if (open) {
            dispatch(getDriverPerformanceDriverSearchEvents({}))
        }
    }, [open, formData])

    useEffect(() => {
        if (open) {
            const { startDate, endDate } = timeFrame;
            let date = getInitialRange(timeFrame?.option?.id, startDate, endDate);

            // When custom timeframe, cap the range to 30 days
            if (timeFrame?.option?.id === 'custom' && date?.start && date?.end) {
                const start = moment(date.start);
                const end = moment(date.end);
                const diffDays = end.diff(start, 'days');

                if (diffDays > 29) {
                    date.end = start.clone().add(29, 'days').endOf('day').toDate();
                }
            }

            let currentDateRange = {
                start: date?.start,
                end: date?.end
            }

            setTempFilters({
                ...tempFilters,
                rangeDate: currentDateRange,
                timeFrame: timeFrame?.option?.id
            });
        }
    }, [timeFrame?.startDate, timeFrame?.endDate]);

    useEffect(() => {
        return () => {
            dispatch(reset())
        }
    }, [])

    const clearFilters = () => {
        const conditions = [{
            range: {
                fields: ["rangeDate"],
                GTE: moment.utc().subtract(7, 'days').startOf('day').toDate(),
                LTE: moment.utc().subtract(1, 'days').endOf('day').toDate()
            },
            page: 1,
            openSearch: formData?.openSearch || ""
        }];

        //Refresh table with initial conditions
        tableRef.refresh({ _conditions: conditions });

        const defaultPreferences = ["rangeDate", "timeFrame", "events", "tags", "drivers", "riskScore"]
            .map(key => ({ key, value: null }));

        //Preferences
        savePreferences(defaultPreferences)
        const clearedData = {
            ...initialState,
            riskScore: null,
            openSearch: ""
        };

        // Clear entitySelected FIRST
        setEntitySelected([]);

        // Then update states
        setFormData(clearedData);
        setTempFilters(clearedData);

        // Reset TimeFrame component
        timeFrame.reset();

        // Force form reset with cleared values
        formRef?.setValues(clearedData);
    }

    const allowOnlyNumbers = (value) => {
        return value.replace(/[^0-9]/g, "");
    }

    return (
        <SidePanel
            isOpen={open}
            onToggle={() => dispatch(openModalAdvancedFilters({ open: !open }))}
            style={{ zIndex: 2, width: '380px', transform: 'translateX(380px)' }}
            classSidePanel={hasFilters() ? "dp-driver-tab-advanced-filters-available" : ""}
        >
            <SidePanelBody>
                <Grid container direction="row" justifyContent="space-between" alignItems="center">
                    <Grid item>
                        <h2 style={{ margin: 0 }}>
                            {messages['workOrder_tab_advancedFilters_title']}
                        </h2>
                        <span
                            className='btn-link'
                            onClick={() => {
                                if (!loading) {
                                    clearFilters()
                                }

                            }}
                            style={{ cursor: loading ? 'not-allowed' : 'pointer' }}
                        >
                            {messages['workOrder_tab_advancedFilters_clearAll']}
                        </span>
                    </Grid>
                </Grid>
                <div style={{ width: 330, marginTop: 10 }}>
                    <Form
                        ref={formRef.ref}
                        loading={loadingEvents || loading}
                        onValidSubmit={() => {
                            // Reset page and apply temporary filters
                            const filtersWithPage = { ...tempFilters, page: 1 };

                            // Apply temporary filters to formData
                            setFormData(filtersWithPage);

                            // Save all preferences when applying filters
                            savePreferences([
                                {
                                    key: "rangeDate",
                                    value: tempFilters.rangeDate
                                },
                                {
                                    key: "timeFrame",
                                    value: tempFilters.timeFrame
                                },
                                {
                                    key: "events",
                                    value: tempFilters.events
                                },
                                {
                                    key: "drivers",
                                    value: tempFilters.drivers
                                },
                                {
                                    key: "tags",
                                    value: tempFilters.tags
                                },
                                {
                                    key: "riskScore",
                                    value: tempFilters.riskScore
                                }
                            ]);
                            // Pass tempFilters directly to avoid issues with asynchronous state
                            applyChanges(filtersWithPage);
                        }}
                        onChangeValues={() => { }}
                        className="dp-driver-tab-advanced-filters"
                    >
                        <Form.Item
                            name="timeFrame"
                            label={messages['driver_performance_driver_filters_timeFrame']}
                            style={{ marginBottom: 10 }}
                        >
                            <div style={{ width: 330, marginTop: -10 }} className="dp-driver-tab-advanced-filters-timeframe">
                                <TimeFrame
                                    isRequired
                                    filterSwitch={false}
                                    {...timeFrame}
                                    maxDate={moment()}
                                    dateFormat={dateformat || 'Y/m/d'}
                                    withTime={false}
                                    openDateRangePickerModalAutomatically={false}
                                    startDate={moment(tempFilters?.rangeDate?.start)}
                                    endDate={moment(tempFilters?.rangeDate?.end)}
                                    customRangeDate={30}
                                />
                            </div>
                        </Form.Item>
                        <div style={{ marginBottom: 10 }}>
                            <Input
                                name="riskScore"
                                label={messages['driver_performance_driver_filters_riskScore']}
                                value={tempFilters?.riskScore ?? ''}
                                onChange={(value) => {
                                    let currentValue = value ? Number(allowOnlyNumbers(value)) : null
                                    if (currentValue !== null && (currentValue <= 0 || currentValue > 100)) {
                                        return;
                                    }
                                    setTempFilters({ ...tempFilters, riskScore: currentValue })
                                }}
                                placeholder={messages['driver_performance_driver_filters_riskScore']}
                                maxLength={3}
                            />
                        </div>
                        <div>
                            <div className="driver-tab-tags" style={{ width: '100%', overflow: "auto", marginTop: 5 }}>
                                <div className="input-gtc-rc-label"><label className="label-gtc-rc">{messages['driver_performance_cases_filters_tags']}</label></div>
                                <TagsManage
                                    entities={entities}
                                    onChange={onChangeEntityTagPicker}
                                    hideTagsWithoutEntities={true}
                                    mode="list"
                                    height={400}
                                    value={entitySelected}
                                    noPills={true}
                                    entitySelected={{ id: "Driver", label: messages["clipRequestAdvancedFilters_drivers"] }}
                                    searchByTag={true}
                                />
                            </div>
                        </div>
                    </Form>
                </div>
            </SidePanelBody>
            <SidePanelFooter>
                <div style={{ display: 'flex', justifyContent: 'end' }}>
                    <Button
                        isDisabled={loadingEvents || loading}
                        style={{ marginRight: 8 }}
                        onClick={() => dispatch(reset({ open: false }))}
                        variant="secondary"
                    >
                        {messages["cancel"]}
                    </Button>
                    <Button
                        isDisabled={loadingEvents || loading || (!timeFrame?.startDate || !timeFrame?.endDate)}
                        onClick={() => formRef?.submit()}
                        variant="primary"
                    >
                        {messages["apply"]}
                    </Button>
                </div>
            </SidePanelFooter>
        </SidePanel>
    )
}

export default AdvancedFilters
